import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaUtensils, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import '../styles/CloudKitchenDetail.css';
import { formatTodayHours } from '../utils/formatTodayHours';
import ItemModal from '../components/ItemModal';
import { StoreContext } from '../context/StoreContext';
import { isCloudKitchenOpen } from '../utils/isCloudKitchenOpen';
import { fetchCloudKitchenById } from '../api/apiService';
import { formatPrice } from '../utils/formatPrice';
// ✅ Import the useClosingStatus hook
import { useClosingStatus } from '../utils/useClosingStatus';

const CLOUD_KITCHEN_CATEGORIES = [
    { label: 'All', value: 'all' },
    { label: 'Veg', value: 'veg' },
    { label: 'Non-Veg', value: 'nonveg' },
    { label: 'Desserts', value: 'desserts' },
];

const CloudKitchenDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { cart, addItemToCart, updateItemQuantity } = useContext(StoreContext);
    const [kitchen, setKitchen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const fetchKitchenDetails = async () => {
            if (!id) {
                setError("Cloud Kitchen ID is missing.");
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const response = await fetchCloudKitchenById(id);
                setKitchen(response.data);
            } catch (err) {
                setError('Failed to load cloud kitchen details.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchKitchenDetails();
    }, [id]);

    // ✅ Prepare timings object for the hook
   
    const timings = React.useMemo(() => {
        return kitchen?.operatingHours || kitchen?.timings || null;
    }, [kitchen]);


    const { isClosingSoon, minutesLeft, nextDayDeliveryTime } = useClosingStatus(
  timings,
  kitchen?.location?.country
);

    const handleOpenModal = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleItemAdd = (itemPayload) => {
        // If the item supports spicy levels and no spicyLevel is set, default to 'medium'
        if (itemPayload.hasSpicyLevels && !itemPayload.hasOwnProperty('spicyLevel')) {
            itemPayload.spicyLevel = 'medium';
        }

        addItemToCart({
            ...itemPayload,
            item_id: itemPayload._id,
            vendor_id: kitchen._id,
            vendor_type: 'cloud_kitchen',
        });
        setShowModal(false);
    };

    const business = kitchen;
    const currency = business?.currency;
    const currencySymbol = currency === 'CAD' ? 'CAD' : (currency === 'USD' ? '$' : '₹');

    if (loading) {
        return <div className="detail-container loading-state"><FaSpinner className="spinner" /> Loading cloud kitchen...</div>;
    }

    if (error || !kitchen) {
        return (
            <div className="detail-container error-state">
                <p className="error-message">{error || "Cloud kitchen not found."}</p>
                <Link to="/" className="back-link"><FaArrowLeft /> Back to Home</Link>
            </div>
        );
    }

    const bannerImg = kitchen.image || 'https://via.placeholder.com/600x200?text=Cloud+Kitchen';
    const locationStr = kitchen.location ? [kitchen.location.street, kitchen.location.city].filter(Boolean).join(', ') : '';
    const open = isCloudKitchenOpen(
  timings,
  kitchen?.location?.country
);


    // ✅ UPDATE: Filter out items where isAvailable is not true
    const rawItems = kitchen.items || [];
    const filteredItems = rawItems.filter(item => {
        // 1. Must be available (Blue Switch)
        if (item.isAvailable !== true) return false;

        // 2. Category Filter
        if (selectedCategory === 'all') return true;
        return (item.type || '').toLowerCase().includes(selectedCategory);
    });

    return (
        <motion.div className="cloud-kitchen-detail-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="detail-header">
                <button className="back-button" onClick={() => navigate(-1)}><FaArrowLeft /></button>
                <img src={bannerImg} alt={kitchen.name} className="detail-banner-img" />
                <div className="detail-overlay">
                    <h1 className="detail-name">{kitchen.name}</h1>
                    <p className="detail-info"><FaUtensils /> {kitchen.cuisine} • <FaMapMarkerAlt /> {locationStr}</p>
                </div>
            </div>

            {/* ✅ Insert Closing Soon Banner */}
            {isClosingSoon && (
                <div className="closing-banner-wrapper">
                    <motion.div 
                        className="closing-alert-banner"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="closing-alert-content">
                            <span className="closing-icon">⚠️</span>
                            <div className="closing-text">
                                <strong>Closing in {minutesLeft} minutes!</strong>
                                <span>Orders placed now will be delivered tomorrow by <b>{nextDayDeliveryTime}</b>.</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            <div className="detail-info-bar">
                 <div className="detail-info-box timings">
                    <span className="detail-info-label">Timings</span>
                    <span className="detail-info-value">
                        {formatTodayHours(timings)}
                    </span>
                </div>

                <span
                    style={{
                        alignSelf: 'center',
                        margin: '0 22px',
                        background: open ? '#eafaf1' : '#fdeaea',
                        color: open ? '#27ae60' : '#e74c3c',
                        borderRadius: 12,
                        fontWeight: 700,
                        fontSize: '1.15rem',
                        padding: '6px 22px',
                        boxShadow: '0 1px 4px #e52e7133',
                        letterSpacing: '0.5px',
                        verticalAlign: 'middle',
                        height: 36,
                        display: 'inline-flex',
                        alignItems: 'center'
                    }}
                >
                    {open ? 'Opened' : 'Closed'}
                </span>
                <div className="detail-info-box delivery">
                    <span className="detail-info-label">Delivery Time</span>
                    <span className="detail-info-value">
                        {kitchen.deliveryTime || 'N/A'}
                    </span>
                </div>
            </div>

            <div className="detail-content">
                <h2 className="menu-heading">Menu</h2>
                <div className="menu-category-tabs">
                    {CLOUD_KITCHEN_CATEGORIES.map(cat => (
                        <button
                            key={cat.value}
                            className={`menu-category-tab${selectedCategory === cat.value ? ' active' : ''}`}
                            onClick={() => setSelectedCategory(cat.value)}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
                {filteredItems && filteredItems.length > 0 ? (
                    <div className="menu-grid">
                        {filteredItems.map((item) => {
                            const itemId = item._id?.toString?.() || item._id;
                            const cartItem = cart?.items?.find(
                                ci => ci.item_id?.toString() === itemId?.toString()
                            );
                            const qty = cartItem ? cartItem.quantity : 0;
                            return (
                                <motion.div key={itemId} className="menu-item-card" onClick={() => open && handleOpenModal(item)}>
                                    <img src={item.image || 'https://via.placeholder.com/120'} alt={item.name} className="menu-item-img" />
                                    <div className="menu-item-info">
                                        <h3 className="menu-item-name">{item.name}</h3>
                                        <div className="menu-item-footer">
                                            <span className="menu-item-price">
                                                {formatPrice(item.price, currency, currencySymbol)}
                                            </span>
                                            {qty === 0 ? (
                                                <button
                                                    className="add-to-cart-btn"
                                                    disabled={!open}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (open) handleItemAdd({ ...item, quantity: 1 });
                                                    }}
                                                >
                                                    Add to Cart
                                                </button>
                                            ) : (
                                                <div className="qty-controls" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => {
                                                            if (qty > 1) {
                                                                updateItemQuantity(cartItem._id, qty - 1);
                                                            } else {
                                                                updateItemQuantity(cartItem._id, 0);
                                                            }
                                                        }}
                                                    >−</button>
                                                    <span className="qty-value">{qty}</span>
                                                    <button
                                                        className="qty-btn"
                                                        onClick={() => updateItemQuantity(cartItem._id, qty + 1)}
                                                    >+</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="no-menu-items">No menu items available in this category.</p>
                )}
            </div>

            {showModal && selectedItem && (
                <ItemModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    item={selectedItem}
                    handleAddToCart={handleItemAdd}
                    cart={cart}
                    vendor_id={kitchen._id}
                    vendor_type="cloud_kitchen"
                    currency={currency}
                    currencySymbol={currencySymbol}
                />
            )}
        </motion.div>
    );
};

export default CloudKitchenDetail;
